import { useState } from 'react';
import Link from 'next/link';

import { ListingCard } from '@components/article/ListingCard';
import {
  ALL_TAB_ID,
  ALL_TAB_LABEL,
  cardLabelFor,
  filterItemsByTab,
  tabsWithItems
} from '@lib/homepage/tabs';
import type { HomepageTab } from '@lib/homepage/tabs';
import type { ListingItem } from '@lib/listings/types';

export const ALL_ARTICLES_PATH = '/category/latest';
const HEADING_ID = 'latest-articles-heading';

type TabButtonProps = {
  label: string;
  isActive: boolean;
  onClick: () => void;
};

const TabButton = ({ label, isActive, onClick }: TabButtonProps) => (
  <button
    type="button"
    aria-pressed={isActive}
    onClick={onClick}
    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-open-sans text-sm font-semibold leading-5 transition-colors ${
      isActive
        ? 'bg-neutral-800 text-white'
        : 'bg-neutral-200 text-neutral-800 hover:bg-neutral-300'
    }`}
  >
    {label}
  </button>
);

type LatestArticlesProps = {
  heading: string;
  items: ListingItem[];
  tabs: HomepageTab[];
  /** Cards shown per tab before the reader is sent to the full listing. */
  cardCount: number;
};

/**
 * The homepage editorial grid: a heading, one filter tab per strand that has
 * something behind it, the newest cards under the active tab and a quiet
 * link out to the full listing. Filtering is client-side over the items the
 * page was built with.
 */
export const LatestArticles = ({
  heading,
  items,
  tabs,
  cardCount
}: LatestArticlesProps) => {
  const [activeTabId, setActiveTabId] = useState(ALL_TAB_ID);
  const visibleTabs = tabsWithItems(items, tabs);
  const matchingItems = filterItemsByTab(items, tabs, activeTabId);
  const shownItems = matchingItems.slice(0, cardCount);
  const countNoun = matchingItems.length === 1 ? 'article' : 'articles';

  return (
    <section
      aria-labelledby={HEADING_ID}
      className="px-2 pt-6 pb-10 sm:px-6 lg:px-8 lg:pt-10 lg:pb-14"
    >
      <div className="flex min-h-[42px] flex-wrap items-end justify-between gap-6">
        <h2
          id={HEADING_ID}
          data-cy="homepage-latest-article-heading"
          className="m-0 font-sans text-2xl font-bold leading-8 text-neutral-800 lg:text-[28px] lg:leading-9"
        >
          {heading}
        </h2>
        {visibleTabs.length > 0 && (
          <div
            role="group"
            aria-label="Filter articles"
            className="-mx-2 flex gap-1.5 overflow-x-auto px-2 scrollbar-hide lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0"
          >
            <TabButton
              label={ALL_TAB_LABEL}
              isActive={activeTabId === ALL_TAB_ID}
              onClick={() => setActiveTabId(ALL_TAB_ID)}
            />
            {visibleTabs.map((tab) => (
              <TabButton
                key={tab.id}
                label={tab.label}
                isActive={activeTabId === tab.id}
                onClick={() => setActiveTabId(tab.id)}
              />
            ))}
          </div>
        )}
      </div>
      <div className="mt-7 grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6 xl:grid-cols-3">
        {shownItems.map((item) => (
          <ListingCard
            key={item.href}
            item={item}
            label={cardLabelFor(item, tabs)}
          />
        ))}
      </div>
      <footer className="mt-7 flex items-center justify-between gap-4 border-t border-neutral-200 pt-5">
        <span className="font-open-sans text-[13px] text-neutral-600">
          {`Showing ${shownItems.length} of ${matchingItems.length} ${countNoun}`}
        </span>
        <Link
          href={ALL_ARTICLES_PATH}
          className="inline-flex items-center gap-2 font-sans text-[15px] font-semibold text-neutral-800 transition-colors hover:text-primary-600"
        >
          All articles
          <span
            aria-hidden="true"
            className="inline-block h-[7px] w-[7px] rotate-45 border-t-2 border-r-2 border-current"
          />
        </Link>
      </footer>
    </section>
  );
};
